/**
 * Single source of truth for the `events_local` primary key.
 *
 * Three code paths write rows into the local `events_local` mirror:
 *   1. `queueEvent` — optimistic insert of a locally-created event;
 *   2. `pull()`     — the confirmed copy fetched from the server;
 *   3. the fiscal-photo import writeback (CapturePhotoPage) — events the edge
 *      created server-side, applied immediately so the UI doesn't wait for sync.
 *
 * All three MUST key the row on the SAME value, otherwise the same logical event
 * lands as two distinct rows and any per-lot aggregation (giacenza) counts it
 * twice. The canonical key is the event's `client_uuid`; the server `id` is only
 * a fallback for the (defensive) case of a row that somehow lacks one.
 *
 * Regression origin — T-311 (2026-06-25, LE BOCCERIE): the import writeback keyed
 * on the server `id` while pull keyed on `client_uuid`, so the device that ran
 * the import ended up with two rows for each imported event and showed double
 * stock (e.g. 3068 = 2×1534) until a full re-sync (logout/login) wiped the local
 * cache. The bug was timing-amplified on slow devices (writeback and pull racing
 * to insert), but the real cause was structural: two writers, two keys. Funnelling
 * every writer through this one function makes the keys impossible to disagree.
 */
export type EventsLocalKeySource = 'client_uuid' | 'server_id';

/**
 * Come `eventsLocalRowId`, ma riporta ANCHE quale chiave è stata usata.
 *
 * Serve alla strumentazione T-312/#4: ogni writer di `events_local` logga
 * questo `source`. La classe-bug di T-311 (stesso evento logico su due righe)
 * si manifesta quando due writer keyano lo STESSO evento su chiavi diverse →
 * `source === 'server_id'` è il campanello d'allarme (il fallback che causò
 * T-311). Funzione PURA: nessun side-effect, nessun import pesante → resta
 * sicura da importare anche nei test.
 */
export function eventsLocalRowIdDetailed(
  ev: { client_uuid?: unknown; id?: unknown },
): { id: string; source: EventsLocalKeySource } {
  if (typeof ev.client_uuid === 'string' && ev.client_uuid.length > 0) {
    return { id: ev.client_uuid, source: 'client_uuid' };
  }
  if (typeof ev.id === 'string' && ev.id.length > 0) {
    return { id: ev.id, source: 'server_id' };
  }
  throw new Error('eventsLocalRowId: event has neither a client_uuid nor an id');
}

export function eventsLocalRowId(ev: { client_uuid?: unknown; id?: unknown }): string {
  return eventsLocalRowIdDetailed(ev).id;
}

/**
 * Identity pair required by confirmed server writebacks: the local primary key
 * remains client_uuid, while server_id stores the immutable server id.
 */
export function confirmedEventLocalIdentity(
  ev: { client_uuid?: unknown; id?: unknown },
): { id: string; serverId: string; source: EventsLocalKeySource } {
  const key = eventsLocalRowIdDetailed(ev);
  if (typeof ev.id !== 'string' || ev.id.length === 0) {
    throw new Error('eventsLocalRowId: confirmed event has no server id');
  }
  return {
    id: key.id,
    serverId: ev.id,
    source: key.source,
  };
}
